import {
	Paper,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Zoom,
} from '@mui/material';
import { Avatar, Table, Checkbox } from 'flowbite-react';
import React, { useState } from 'react';
import { stringAvatar } from 'shared-utils';
import { GetUsersForUniversityAPIResponse, ProctoringStatus, UIUser, UserCareerPathType } from 'types';
import VerifiedTooltip from '../profile/VerifiedTooltip';
import { LightTooltip } from '../recruiterPositions/RecruiterCandidateCardHeader';
import { useThemeContext } from 'src/context/ThemeProvider';
import MatchPercentageCircularBar from './MathPercentageCirularBar';
import { Loader } from 'ui';
import UserProfile from '../profile/UserProfile';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import { FaArrowLeftLong, FaLink } from 'react-icons/fa6';
import { useGetUserProfile } from 'src/hooks/profile.hooks';
import { APP_ENDPOINT } from 'src/config/env.config';

type candidateTableColumnHeaderType = {
	id: string;
	label: string;
	align: 'left' | 'center' | 'right' | 'justify' | 'inherit' | undefined;
	minWidth: string;
	info?: string;
};

interface Props {
	students: GetUsersForUniversityAPIResponse;
	count: number;
	page: number;
	setPage: (page: number) => void;
	rowsPerPage: number;
	setRowsPerPage: (rowsPerPage: number) => void;
}

const StudentReportList = ({ students, count, page, setPage, rowsPerPage, setRowsPerPage }: Props) => {
	const { themeMode } = useThemeContext();
	const bulkSelectedCandidates: any[] = [];
	const [searchParams, setSearchParams] = useSearchParams();
	const studentHandler = searchParams.get('student');
	const tableColumns: candidateTableColumnHeaderType[] = [
		{ id: 'student', label: 'Student', align: 'left', minWidth: 'fit-content' },
		{ id: 'public_profile_link', label: 'Public Profile', align: 'center', minWidth: 'fit-content' },
		{ id: 'degree', label: 'Degree', align: 'center', minWidth: 'fit-content' },
		{ id: 'prospect', label: 'Specialization', align: 'center', minWidth: 'fit-content' },
		{ id: 'assessment_type', label: 'Assessment Type', align: 'center', minWidth: 'fit-content' },
		{ id: 'overall_score', label: 'Overall Score', align: 'center', minWidth: 'fit-content' },
		{
			id: 'credibility',
			label: 'Credibility Score',
			align: 'center',
			minWidth: 'fit-content',
			info: 'Credibility Index rates candidates as Low, Medium, or High, with High indicating the lowest likelihood of cheating.',
		},
		{ id: 'category_score', label: 'Category Score(/1000)', align: 'left', minWidth: 'fit-content' },
		{ id: 'predicted_salary', label: 'Predicted Salary', align: 'left', minWidth: 'fit-content' },
	];
	const userProfile = useGetUserProfile(studentHandler ?? '');

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const getBadgeColor = (studentData?: ProctoringStatus | 'NA') => {
		if (studentData === ProctoringStatus.High) {
			return 'bg-green-100 text-green-800';
		}
		if (studentData === ProctoringStatus.Medium) {
			return 'bg-[#E1EFFE] text-[#1E429F]';
		}
		if (studentData === ProctoringStatus.Low) {
			return 'bg-red-100 text-red-800';
		}
		return '';
	};

	const getBgColor = (studentData?: UserCareerPathType | 'NA') => {
		if (studentData === UserCareerPathType.SOFTWARE_DEVELOPMENT) {
			return 'bg-sky-100 text-[#1E429F]';
		}
		if (studentData === UserCareerPathType.DATA_ANALYTICS) {
			return 'bg-purple-100 text-[#5521B5]';
		}
		if (studentData === UserCareerPathType.SDET) {
			return 'bg-green-100 text-[#008000]';
		}
		if (studentData === UserCareerPathType.TECH_SUPPORT) {
			return 'bg-teal-100 text-[#005B5B]';
		}
		if (studentData === UserCareerPathType.OPERATIONS) {
			return 'bg-gray-100 text-[#6C757D]';
		}
		if (studentData === UserCareerPathType.BD_EXECUTIVE) {
			return 'bg-orange-100 text-[#FF8C00]';
		}
		return '';
	};

	const tableRowContent = (tableRow: string, studentData: UIUser) => {
		const maxLength = 15;
		switch (tableRow.toLowerCase()) {
			case 'student': {
				const name = studentData?.name;
				const email = studentData?.email;
				const phone = studentData?.phone;
				const avatar = studentData?.profile?.proctoringImageUrl;

				const isTrusted = studentData?.flags?.isTrusted;
				return (
					<div className="flex grow-2 gap-2">
						<div className="min-w-[40px]">
							<Avatar
								rounded
								size="md"
								img={avatar || ''}
								placeholderInitials={stringAvatar(name.trim().toUpperCase() || '')}
							/>
						</div>

						<div>
							<div className="dark:text-white">
								<div className="flex gap-1">
									{name.length > maxLength ? (
										<div>
											<Tooltip title={name} placement="top">
												<p
													onClick={() => {
														searchParams.set('student', studentData.handler);
														setSearchParams(searchParams);
													}}
													className="cursor-pointer underline text-[18px] font-[600] text-[#1C1C28] flex items-center gap-1 capitalize"
												>
													<span>{name?.toLowerCase()?.substring(0, maxLength)}...</span>
													<span>
														{isTrusted ? (
															<div className="flex flex-col justify-center">
																<LightTooltip
																	light={!(themeMode === 'dark')}
																	title={<VerifiedTooltip />}
																	placement="bottom"
																	arrow={false}
																>
																	<img src="/assets/verified-tag.svg" alt={'verified-tag'} width={14} height={14} />
																</LightTooltip>
															</div>
														) : null}
													</span>
												</p>
											</Tooltip>
											<p className="text-[#555770] text-xs">{email}</p>
											<p className="text-[#555770] text-xs">{phone}</p>
										</div>
									) : (
										<div>
											<p
												onClick={() => {
													searchParams.set('student', studentData.handler);
													setSearchParams(searchParams);
												}}
												className="underline cursor-pointer text-[18px] font-[600] text-[#1C1C28] flex items-center gap-1 capitalize"
											>
												<span>{name?.toLowerCase()}</span>
												<span>
													{isTrusted ? (
														<div className="flex flex-col justify-center">
															<LightTooltip
																light={!(themeMode === 'dark')}
																title={<VerifiedTooltip />}
																placement="bottom"
																arrow={false}
															>
																<img src="/assets/verified-tag.svg" alt={'verified-tag'} width={14} height={14} />
															</LightTooltip>
														</div>
													) : null}
												</span>
											</p>
											<p className="text-[#555770] text-xs">{email}</p>
											<p className="text-[#555770] text-xs">{phone}</p>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				);
			}
			case 'public_profile_link': {
				return (
					<div className="m-auto">
						<a
							href={`https://levelup.masaischool.com/jobseeker/profile/${studentData.handler}?company=true`}
							target="_blank"
							rel="noreferrer"
						>
							<FaLink className="mx-auto" />
						</a>
					</div>
				);
			}
			case 'assessment_type': {
				const assessmentType = studentData?.userPreference?.careerPath || 'NA';
				const formatCareerPathType = (type: UserCareerPathType): string => {
					const formatted = type
						.replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capital letters
						.replace(/_/g, ' ') // Replace underscores with spaces
						.split(' ') // Split into words
						.map(word => {
							// Capitalize first letter for normal words
							if (word === 'sdet') return 'SDET'; // Special case for SDET
							if (word === 'bd') return 'BD'; // Special case for BD
							return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
						})
						.join(' '); // Join back into a string

					return formatted;
				};

				return (
					<div className="flex items-start leading-5 text-center">
						<div
							className={classNames(
								'self-stretch rounded-lg m-auto py-1 px-[20px] text-base font-medium',
								getBgColor(assessmentType),
							)}
						>
							{formatCareerPathType(assessmentType as UserCareerPathType)}
						</div>
					</div>
				);
			}

			case 'overall_score': {
				return (
					<div className="w-[150px] m-auto">
						<MatchPercentageCircularBar value={Math.round(studentData?.evaluation?.level || 0)} />
					</div>
				);
			}
			case 'credibility': {
				const credibility = students?.usersWithCredibility?.[studentData?._id];
				return (
					<div className="flex items-start leading-5 text-center">
						<div
							className={classNames(
								'self-stretch rounded-lg m-auto py-1 px-[20px] text-base font-medium',
								getBadgeColor(credibility),
							)}
						>
							{credibility}
						</div>
					</div>
				);
			}
			case 'category_score': {
				const groupedSkillRatings = students?.groupedSkillRatings;
				let groupedSkillRating;
				if (groupedSkillRatings) {
					groupedSkillRating = groupedSkillRatings[studentData?._id];
				}
				return (
					<div className="mx-auto flex items-center space-x-2">
						{groupedSkillRating &&
							groupedSkillRating['Technical_Skills'] &&
							groupedSkillRating['Technical_Skills']?.avg > 0.01 && (
								<>
									<div className="flex flex-col text-center items-center whitespace-nowrap">
										<p className="text-xs text-[#555770]">Technical</p>
										<p className="text-sm text-[#555770]">{groupedSkillRating['Technical_Skills'].avg.toFixed(2)}</p>
									</div>
									<div className="w-[1px] h-10 bg-[#EBEBF0] mx-2"></div>
								</>
							)}
						{groupedSkillRating &&
							groupedSkillRating['Theoretical_Knowledge'] &&
							groupedSkillRating['Theoretical_Knowledge']?.avg > 0.01 && (
								<>
									<div className="flex flex-col text-center items-center whitespace-nowrap">
										<p className="text-xs text-[#555770]">Theoretical</p>
										<p className="text-sm text-[#555770]">
											{groupedSkillRating['Theoretical_Knowledge'].avg.toFixed(2)}
										</p>
									</div>
									<div className="w-[1px] h-10 bg-[#EBEBF0] mx-2"></div>
								</>
							)}
						{groupedSkillRating &&
							groupedSkillRating['Aptitude_Skills'] &&
							groupedSkillRating['Aptitude_Skills']?.avg > 0.01 && (
								<>
									<div className="flex flex-col text-center items-center whitespace-nowrap">
										<p className="text-xs text-[#555770]">Aptitude</p>
										<p className="text-sm text-[#555770]">{groupedSkillRating['Aptitude_Skills'].avg.toFixed(2)}</p>
									</div>
									<div className="w-[1px] h-10 bg-[#EBEBF0] mx-2"></div>
								</>
							)}
						{groupedSkillRating &&
							groupedSkillRating['Soft_Skills'] &&
							groupedSkillRating['Soft_Skills']?.avg > 0.01 && (
								<>
									<div className="flex flex-col text-center items-center whitespace-nowrap">
										<p className="text-xs text-[#555770]">Soft Skill</p>
										<p className="text-sm text-[#555770]">{groupedSkillRating['Soft_Skills'].avg.toFixed(2)}</p>
									</div>
								</>
							)}
					</div>
				);
			}
			case 'degree': {
				const degree =
					studentData?.pifMeta?.underGraduate?.degree || studentData?.pifMeta?.postGraduate?.degree || 'NA';
				return (
					<div className="max-w-[130px] flex flex-col text-center mx-auto">
						<p>{degree}</p>
					</div>
				);
			}
			case 'prospect': {
				const specialization =
					studentData?.pifMeta?.underGraduate?.specialisation ||
					studentData?.pifMeta?.postGraduate?.specialisation ||
					'NA';
				return (
					<div className="max-w-[130px] flex flex-col text-center mx-auto">
						<p>{specialization}</p>
					</div>
				);
			}
			case 'predicted_salary': {
				const minPredictedSalary = studentData?.evaluation?.predictedSalary?.min;
				const maxPredictedSalary = studentData?.evaluation?.predictedSalary?.max;
				const salaryRangeToShow =
					typeof minPredictedSalary === 'number' && typeof maxPredictedSalary === 'number'
						? `₹${minPredictedSalary} - ${maxPredictedSalary}LPA`
						: 'NA';
				return (
					<div className="flex flex-col w-fit items-start">
						<p className="font-[400] font-inter text-[#8F90A6] text-xs">Salary Range</p>
						<p>{salaryRangeToShow}</p>
					</div>
				);
			}
		}
	};

	const isLoading = false;

	return isLoading ? (
		<div className="flex items-center justify-center relative top-[40vh]">
			<Loader />
		</div>
	) : studentHandler ? (
		<>
			{userProfile?.isLoading ? null : (
				<div className="w-full text-left block max-w-[1024px] mx-auto border-b border-[#E0E0E0] pb-3">
					<button onClick={() => setSearchParams({})} className="w-fit text-left flex gap-2 items-center">
						<div>
							<FaArrowLeftLong size={20} />
						</div>
						<div className="text-xl font-[600] font-inter text-[#1C1C28] capitalize">
							{userProfile?.data?.userDetail?.name?.toLowerCase() + "'s"} Detailed Profile
						</div>
					</button>
				</div>
			)}
			<div
				className={`${studentHandler ? 'h-full md:overflow-y-scroll grow-2 w-full md:rounded-[12px] md:px-3 ' : 'hidden'
					} my-[24px]`}
			>
				{studentHandler ? (
					<div className="">
						<UserProfile
							forceMobileView={false}
							userHandler={students && studentHandler}
							hasShownInterest={false}
							showBannerBg={false}
							profileBackground="!bg-[#F7F9FB]"
						/>
					</div>
				) : null}
			</div>
		</>
	) : (
		<div className="flex max-w-[1440px] mx-auto">
			<Paper
				sx={{
					mt: '24px',
					bg: 'white',
					width: '100%',
					overflow: 'hidden',
					borderRadius: '8px',
					isolation: 'isolate',
					boxShadow: '0px 1px 2px -1px rgba(0, 0, 0, 0.10), 0px 1px 3px 0px rgba(0, 0, 0, 0.10)',
				}}
			>
				<TableContainer sx={{ minHeight: 440, '&>div>div': { display: 'none' } }}>
					<Table aria-label="sticky table">
						<TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
							<TableRow
								sx={{
									'&': {
										background: '#F2F2F5',
										'[data-mode="dark"] &': {
											background: '#18222D',
										},
									},
								}}
							>
								{tableColumns.map((column, id) =>
									column?.info ? (
										<Tooltip key={id} title={column?.info} arrow>
											<TableCell
												className="uppercase text-[#1F3343] font-inter font-[500] text-sm"
												align={column.align}
												style={{ minWidth: column.minWidth }}
											>
												{column.label}
											</TableCell>
										</Tooltip>
									) : (
										<TableCell
											className="uppercase text-[#1F3343] font-inter font-[500] text-sm"
											key={id}
											align={column.align}
											style={{ minWidth: column.minWidth }}
										>
											{column.label}
										</TableCell>
									),
								)}
							</TableRow>
						</TableHead>
						<TableBody
							sx={{
								'&': {
									'[data-mode="dark"] &': {
										background: '#18222D',
									},
								},
							}}
						>
							{students?.users?.map((student, id) => {
								const isSelected = bulkSelectedCandidates.find(
									bulkCandidate => bulkCandidate.user.handler === student.handler,
								)
									? true
									: false;

								return (
									<TableRow
										onClick={() => {
											// searchParams.set('student', student.handler);
											// setSearchParams(searchParams);
										}}
										aria-checked={isSelected}
										hover
										role="checkbox"
										tabIndex={-1}
										key={student._id}
									>
										<Zoom mountOnEnter unmountOnExit>
											<TableCell align="center" padding="checkbox">
												<Checkbox color="purplePlaced" checked={isSelected} />
											</TableCell>
										</Zoom>
										{tableColumns.map((column, id) => {
											return (
												<TableCell key={student._id + id} align={column.align}>
													{tableRowContent(column.id, student)}
												</TableCell>
											);
										})}
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					sx={{
						'&': {
							background: '#FAFAFC',
							'[data-mode="dark"] &': {
								background: '#18222D',
							},
						},
					}}
					rowsPerPageOptions={[10, 25, 100]}
					component="div"
					count={count}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</Paper>
		</div>
	);
};

export default StudentReportList;
